import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  activateTenant,
  closeTenant,
  createTenant,
  fetchRoleMappings,
  fetchTenantDetail,
  fetchTenants,
  fetchUserActivity,
  patchTenantConfig,
  patchTenantIdp,
  putRoleMappings,
  type CreateTenantInput,
  type RoleMapping,
  type TenantCard,
  type TenantConfigPatch,
  type TenantDetail,
  type TenantIdpConfig,
  type UserActivity,
} from './admin-api';

/** Admin tenants query + mutations (SCRUM-391). One key; mutations invalidate. */
const TENANTS_KEY = ['admin-tenants'] as const;
const tenantDetailKey = (id: string) => ['admin-tenant-detail', id] as const;

export function useTenants(): { tenants: TenantCard[]; isLoading: boolean; isError: boolean } {
  const { data, isLoading, isError } = useQuery<TenantCard[]>({
    queryKey: TENANTS_KEY,
    queryFn: fetchTenants,
    staleTime: 30_000,
    retry: false,
  });
  return { tenants: data ?? [], isLoading, isError };
}

/** Full Manage Account profile for one tenant (SCRUM-433). */
export function useTenantDetail(id: string): {
  detail: TenantDetail | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery<TenantDetail>({
    queryKey: tenantDetailKey(id),
    queryFn: () => fetchTenantDetail(id),
    retry: false,
  });
  return { detail: data, isLoading, isError };
}

/** PATCH the config-backed sections; invalidates both the detail and the card list. */
export function useUpdateTenantConfig(
  id: string,
): UseMutationResult<void, Error, TenantConfigPatch> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: TenantConfigPatch) => patchTenantConfig(id, patch),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantDetailKey(id) });
      await queryClient.invalidateQueries({ queryKey: TENANTS_KEY });
    },
  });
}

export function useCreateTenant(): UseMutationResult<string, Error, CreateTenantInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTenant,
    onSettled: () => queryClient.invalidateQueries({ queryKey: TENANTS_KEY }),
  });
}

export function useCloseTenant(): UseMutationResult<
  void,
  Error,
  { id: string; reason: string; notes: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, notes }) => closeTenant(id, reason, notes),
    onSettled: () => queryClient.invalidateQueries({ queryKey: TENANTS_KEY }),
  });
}

// ── Provisioning completeness (SCRUM-585) ───────────────────────────────────

/** PATCH the tenant's IdP federation config; refreshes detail + cards. */
export function useConfigureTenantIdp(id: string): UseMutationResult<void, Error, TenantIdpConfig> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idp: TenantIdpConfig) => patchTenantIdp(id, idp),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantDetailKey(id) });
      await queryClient.invalidateQueries({ queryKey: TENANTS_KEY });
    },
  });
}

/**
 * Activate & provision (fail-closed server-side): schema + Active status +
 * primary-contact user. Refreshes detail + cards so the status pill flips.
 */
export function useActivateTenant(id: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => activateTenant(id),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantDetailKey(id) });
      await queryClient.invalidateQueries({ queryKey: TENANTS_KEY });
    },
  });
}

const roleMappingsKey = (id: string) => ['admin-role-mappings', id] as const;

/** The tenant's IdP-group → role mapping rules. */
export function useRoleMappings(id: string): {
  mappings: RoleMapping[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery<RoleMapping[]>({
    queryKey: roleMappingsKey(id),
    queryFn: () => fetchRoleMappings(id),
    retry: false,
  });
  return { mappings: data ?? [], isLoading, isError };
}

/** Replace the tenant's role-mapping rules wholesale. */
export function useReplaceRoleMappings(id: string): UseMutationResult<void, Error, RoleMapping[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mappings: RoleMapping[]) => putRoleMappings(id, mappings),
    onSettled: () => queryClient.invalidateQueries({ queryKey: roleMappingsKey(id) }),
  });
}

/** Cross-tenant user-activity list (SCRUM-575), optionally filtered by tenant. */
export function useUserActivity(tenantId?: string): {
  users: UserActivity[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery<UserActivity[]>({
    queryKey: ['admin-user-activity', tenantId ?? 'all'] as const,
    queryFn: () => fetchUserActivity(tenantId),
    staleTime: 30_000,
    retry: false,
  });
  return { users: data ?? [], isLoading, isError };
}
